import { useCallback, useEffect, useRef } from 'react';
import { doc, onSnapshot } from 'firebase/firestore';

import { db } from '../firebase';
import { apiFetch } from '../utils/apiAuth';
import { errorText } from '../utils/apiErrors';

const apiUrl = process.env.REACT_APP_API_URL || '';

// Triggers Cloud Run jobs from an admin screen. Job-status listeners are closed on unmount,
// so leaving the screen mid-job no longer refetches into an unmounted component.
export const useCloudRunJob = () => {
  const listenersRef = useRef(new Set());

  useEffect(() => {
    const listeners = listenersRef.current;
    return () => {
      listeners.forEach((unsub) => unsub());
      listeners.clear();
    };
  }, []);

  // Calls onFinished(status) once the job_logs document reaches completed or failed.
  const watchJob = useCallback((jobId, onFinished) => {
    if (!jobId) return;
    const unsub = onSnapshot(doc(db, 'job_logs', jobId), (snapshot) => {
      if (!snapshot.exists()) return;
      const { status } = snapshot.data();
      if (status === 'completed' || status === 'failed') {
        unsub();
        listenersRef.current.delete(unsub);
        onFinished(status);
      }
    });
    listenersRef.current.add(unsub);
  }, []);

  // POSTs to the job endpoint and opens its log panel; throws a translated Error when refused.
  const startJob = useCallback(async (path, body, { t, onFinished } = {}) => {
    const response = await apiFetch(`${apiUrl}${path}`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(body),
    });
    const data = await response.json().catch(() => ({}));
    if (!response.ok) {
      throw new Error(errorText(data, t, 'Failed to trigger Cloud Run Job'));
    }
    window.dispatchEvent(new CustomEvent('open-job-logs', { detail: { jobId: data.jobId } }));
    if (onFinished) watchJob(data.jobId, onFinished);
    return data;
  }, [watchJob]);

  return { startJob, watchJob };
};
